import { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Grid, Paper, Typography, IconButton, Snackbar, Alert, Tooltip} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import { VscNewFile } from "react-icons/vsc";
import { FaFileExcel } from 'react-icons/fa';

const path = `http://192.168.1.248:3103/api/consignee`;

export default function ConsigneeDetails2({ loggedInUser }) {
    const navigate = useNavigate();
    const userId = loggedInUser?.userId;

    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [selectionModel, setSelectionModel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchData();
        fetchTotalCount();
    }, [paginationModel]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const offset = paginationModel.page * paginationModel.pageSize;
            const res = await axios.get(`${path}/search?mode=search&offset=${offset}&limit=${paginationModel.pageSize}`);
            setRows(res.data || []);
            setSelectionModel([]);
        } catch {
            setSnackbar({ open: true, message: 'Failed to fetch data.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const fetchTotalCount = async () => {
        try {
            const res = await axios.get(`${path}/count`);
            setTotalCount(Number(res.data) || 0);
        } catch {
            console.error('Error fetching consignee count');
        }
    };

    const handleDelete = async () => {
        if (selectionModel.length === 0) return;
        try {
            await axios.post(`${path}/delete`, { userId, objAryId: selectionModel });
            setSnackbar({ open: true, message: 'Deleted successfully.', severity: 'success' });
            fetchData();
        } catch {
            setSnackbar({ open: true, message: 'Delete failed.', severity: 'error' });
        }
    };

    const funExportData = async () => {
        try {
            const res = await axios({
                url: `${path}/search?mode=export`,
                method: 'GET',
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'exportedConsignee.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setSnackbar({ open: true, message: 'Export failed.', severity: 'error' });
        }
    };

    const columns = [
        { field: 'ConName', headerName: 'Consignee Name', flex: 1.2,
            renderCell: (params) => (
                <Typography
                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => navigate(`/main/edit-consignee/${params.row.ConID}`)}
                >
                    {params.value}
                </Typography>
            )
        },
        { field: 'location', headerName: 'Location', flex: 1,
            renderCell: (params) => {
                const { City, State } = params.row;
                return (
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            {City || '—'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {State || '—'}
                        </Typography>
                    </Box>
                );
            }
        },
        { field: 'Pincode', headerName: 'Pincode', flex: 0.4 },
        { field: 'GSTNO', headerName: 'GST No.', flex: 0.6 },
        { field: 'Mobile', headerName: 'Mobile No.', flex: 0.5 },
        { field: 'actions', headerName: 'Actions', flex: 0.2, sortable: false, filterable: false, disableColumnMenu: true, align: 'center', headerAlign: 'center',
            renderCell: (params) => {
                const isRowSelected = selectionModel.includes(params.row.ConID);
                return (
                    <Tooltip title="Delete">
                        <span>
                            <IconButton
                                color="error"
                                onClick={handleDelete}
                                disabled={!isRowSelected} // ✅ enable only if this row is selected
                                size="small"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            }
        }

    ];

    return (
        <Box sx={{ p: 2, mt: 2 }}>
            {/* Toolbar */}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={2}>
                    <Box display="flex" justifyContent="flex-start" gap={1}>
                        <Tooltip title="Add New Consignee">
                            <IconButton component={RouterLink} to="/main/consignee-master" color="primary">
                                <VscNewFile />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>

                {/* Center - Heading */}
                <Grid item xs={8} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Consignee Details
                    </Typography>
                </Grid>

                <Grid item xs={2} sx={{ textAlign: 'right' }}>
                    <Tooltip title="Export to Excel">
                        <IconButton onClick={funExportData} color="success">
                            <FaFileExcel />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            {/* DataGrid */}
            <Paper elevation={0} sx={{ mt: 2, p: 1, bgcolor: '#fff', borderRadius: 2 }}>
                <Box sx={{ height: 'auto', minWidth: '100%' }}>
                    <DataGrid
                        autoHeight
                        rows={rows}
                        columns={columns}
                        getRowId={(r) => r.ConID}
                        checkboxSelection
                        selectionModel={selectionModel}
                        onSelectionModelChange={(newSel) => setSelectionModel(newSel.map(Number))}
                        disableSelectionOnClick
                        loading={loading}
                        pagination
                        pageSize={paginationModel.pageSize}
                        page={paginationModel.page}
                        rowCount={totalCount}
                        paginationMode="server"
                        onPageChange={(page) => setPaginationModel(p => ({ ...p, page }))}
                        onPageSizeChange={(ps) => setPaginationModel(p => ({ ...p, pageSize: ps }))}
                        rowsPerPageOptions={[50, 100, 150]}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#1877F2', // Facebook Blue
                                color: '#FFFFFF',
                                fontWeight: 'bold',
                            },
                            '& .MuiDataGrid-row:nth-of-type(odd)': {
                                backgroundColor: '#FFFFFF', // Odd row: white
                            },
                            '& .MuiDataGrid-row:nth-of-type(even)': {
                                backgroundColor: '#F8FAFC', // Even row: slate-50
                            },
                        }}
                    />
                </Box>
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}